import { Component, OnDestroy, OnInit } from '@angular/core';
import { AbstractFlagrService } from 'bqx-feature-switches';
import { LogLevel, LoggerAbstractService } from 'bqx-logging';
import { NGXLogger } from 'ngx-logger';
import { BehaviorSubject, ReplaySubject, Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { SessionStorage } from 'src/app/enums/sdk-application.enum';
import { IAnswer, IApplication, IQuestionSet } from 'src/app/models/application-form/application-form.model';
import { ApplicationFormAbstractService } from 'src/app/services/application-form/application-form.abstract.service';
import { FormDataAbstractService } from 'src/app/services/form-data/form-data.abstract.service';
import { FormDataService } from 'src/app/services/form-data/form-data.service';
import { SecondaryDataAbstractService } from 'src/app/services/secondary-data/secondary-data.abstract.service';

@Component({
    selector: 'appform-question-set-container',
    templateUrl: './question-set-container.component.html',
    styleUrls: ['./question-set-container.component.css'],
    providers: [{ provide: FormDataAbstractService, useClass: FormDataService }],
    standalone: false
})
export class QuestionSetContainerComponent implements OnInit, OnDestroy {
  private _unsubFormData$ = new Subject<void>();
  private _unsubActiveSet$ = new Subject<void>();
  private _unsubReadOnly$ = new Subject<void>();
  private _objSecDataUnSub$ = new Subject<void>();

  private _isContactInfoReady = false;
  private _isBusinessTypeReady = false;
  private _isProductInfoReady = false;
  private _isGeneralInfoReady = false;

  public formData: IApplication;
  public readOnly: boolean;
  public isAppFormReady$ = new ReplaySubject<boolean>();
  public isAppFormDataReady$ = new Subject<boolean>();
  public activeQuestionSetOrder: number;
  public activeQuestionSet: IQuestionSet;
  public isAppFormRedirected$ = new Subject<boolean>();

  public get isTakingOver(): boolean {
    return this.applicationFormService.isTakingOver;
  }

  public get isProductInfoReady(): boolean {
    return this._isProductInfoReady;
  }

  public get isContactInfoReady(): boolean {
    return this._isContactInfoReady;
  }

  public get isBusinessTypeReady(): boolean {
    return this._isBusinessTypeReady;
  }

  public get isGeneralInfoReady(): boolean {
    return this._isGeneralInfoReady;
  }
  public get isFlagrDone(): boolean {
    return this.flagrService.hasFlagrBeenCalled;
  }

  constructor(
    private applicationFormService: ApplicationFormAbstractService,
    private loggerService: LoggerAbstractService,
    private secondaryDataService: SecondaryDataAbstractService,
    private logger: NGXLogger,
    private flagrService: AbstractFlagrService
  ) {}

  ngOnInit(): void {
    if (sessionStorage.getItem(SessionStorage.SESSION_PREFILL_GUID)) {
      this._isProductInfoReady = true;
      this._isContactInfoReady = true;
      this._isBusinessTypeReady = true;
    }
    this.applicationFormService.createNewApplication();
    this.applicationFormService.application$.pipe(takeUntil(this._unsubFormData$)).subscribe(
      (formData) => {
        if (formData) {
          this.formData = formData;
        }
      },
      (err: string) => {
        this.loggerService.postLogData(LogLevel.Error, 'question-set-container: formData in ngOnInit', err, '');
        this._unsubFormData$.next();
        this._unsubFormData$.complete();
      }
    );
    this.applicationFormService.activeQuestionSet$.pipe(takeUntil(this._unsubActiveSet$)).subscribe(
      (activeSet) => {
        if (activeSet) {
          this.activeQuestionSetOrder = activeSet.order;
          this.activeQuestionSet = activeSet;
          this.markIncompleteBusinessInfo();
          this.saveSecondaryData();
        }
      },
      (err: string) => {
        this.loggerService.postLogData(LogLevel.Error, 'question-set-container: activeQuestionSetOrder in ngOnInit', err, '');
        this._unsubActiveSet$.next();
        this._unsubActiveSet$.complete();
      }
    );
    this.isAppFormRedirected$ = this.applicationFormService.isAppFormRedirected$;
    this._isAppSyncedWithBP$ = this.applicationFormService.isAppSyncedWithBP$;
    this.setReadOnly();
    this.applicationFormService.isAppSyncedWithBP$.subscribe((val) => {
      this.isAppFormReady$.next(val);
    });
  }

  private setReadOnly() {
    this.applicationFormService.readOnlyMode$.pipe(takeUntil(this._unsubReadOnly$)).subscribe((result) => {
      this.readOnly = result;
    });
  }

  private _isAppSyncedWithBP$: BehaviorSubject<boolean>;

  public get isAppSyncedWithBP$(): BehaviorSubject<boolean> {
    return this._isAppSyncedWithBP$;
  }

  public updateApplicationState(event: boolean): void {
    this.isAppFormReady$.next(event);
  }

  public updateFormState(event: IAnswer): void {
    this.applicationFormService.updateAnswer(event);
    this.applicationFormService.updateBusinessInfoState();
  }

  public updateMenuOrder(event: number): void {
    if (event !== undefined || event !== null) {
      this.activeQuestionSetOrder = event;
    }
  }

  private saveSecondaryData(): void {
    if (this.secondaryDataService.isSaveDataAllowed && this.formData?.form?.reference_id) {
      const appFormId = this.formData.form.reference_id.replace(/-/g, '');
      this.secondaryDataService
        .saveSecondaryData(appFormId)
        .pipe(takeUntil(this._objSecDataUnSub$))
        .subscribe(
          (response) => {
            if (response.filepath) this.loggerService.postLogData(LogLevel.Info, 'Save Secondary Data', JSON.stringify({ ...response, quoteNumber: this.formData.form.reference_id }), '');
            else this.loggerService.postLogData(LogLevel.Error, 'Save Secondary Data', JSON.stringify({ ...response, quoteNumber: this.formData.form.reference_id }), '');
            this._objSecDataUnSub$.next();
            this._objSecDataUnSub$.complete();
          },
          (err) => {
            this.loggerService.postLogData(LogLevel.Error, 'secondary-data-service', err, '');
            this._objSecDataUnSub$.next();
            this._objSecDataUnSub$.complete();
          }
        );
    }
  }

  private markIncompleteBusinessInfo() {
    this.formData?.questionSets.list.forEach((qSet) => {
      switch (qSet.question_set.code) {
        case 'progressive_commercial_overview': {
          this._isProductInfoReady = qSet.answers.length > 0;
          break;
        }
        case 'progressive_commercial_contact': {
          this._isContactInfoReady = qSet.answers.length > 0;
          break;
        }
        case 'progressive_commercial_business_type': {
          this._isBusinessTypeReady = qSet.answers.length > 0;
          break;
        }
        case 'progressive_commercial_general': {
          this._isGeneralInfoReady = this.formData?.questionSets.list.length > 4;
          break;
        }
        default:
          break;
      }
    });
  }

  ngOnDestroy(): void {
    this.secondaryDataService.lastFinalClassification = null;
    this.secondaryDataService.secondaryData = null;
    this.applicationFormService.resetSdkApplicationForm();
    this._unsubFormData$.next();
    this._unsubFormData$.complete();
    this._unsubActiveSet$.next();
    this._unsubActiveSet$.complete();
    this._unsubReadOnly$.next;
    this._unsubReadOnly$.complete();
  }
}
